/**
 * MspNotificationsCard — Settings card that lets a tenant admin
 * enable Managed Service Provider request emails on package creation.
 *
 * When enabled, ALICE fires a `Key: value` layout email to the
 * configured To address (with optional CC) every time a package
 * completes — both hand-packaged apps and auto-update-produced ones.
 *
 * Backend contract:
 *   GET /api/tenant/settings/msp-notifications
 *   PUT /api/tenant/settings/msp-notifications
 * Body: { enabled, to_email, cc_email }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Mail } from "lucide-react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";

export default function MspNotificationsCard({ apiBase, authHeaders }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeaders();
        const r = await axios.get(
          `${apiBase}/tenant/settings/msp-notifications`,
          { headers },
        );
        setEnabled(!!r.data?.enabled);
        setTo(r.data?.to_email || "");
        setCc(r.data?.cc_email || "");
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, authHeaders]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const headers = await authHeaders();
      await axios.put(
        `${apiBase}/tenant/settings/msp-notifications`,
        { enabled, to_email: to || null, cc_email: cc || null },
        { headers },
      );
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="msp-notifications-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Mail className="h-5 w-5" />
          Service Desk Request Emails
        </CardTitle>
        <CardDescription>
          When enabled, ALICE emails your Managed Service Desk every time a
          package is created (new or via the auto-update scheduler). Subject
          line follows the pattern:&nbsp;
          <code>ALICE - [Tenant] - [App] [Version]</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Enable service-desk emails</Label>
                <p className="text-xs text-white/60 mt-1">
                  Off by default. Turn on to auto-generate requests on every
                  completed package.
                </p>
              </div>
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="msp-notifications-toggle"
              />
            </div>

            <div className="grid gap-4">
              <div>
                <Label htmlFor="msp-to" className="text-sm">
                  To address <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="msp-to"
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="servicedesk@yourmsp.com"
                  disabled={!enabled}
                  data-testid="msp-notifications-to"
                  className="mt-1"
                />
                <p className="text-xs text-white/50 mt-1">
                  Required when the feature is enabled. Typically your MSP&apos;s
                  intake mailbox (Halo/ServiceNow/Freshservice, etc.).
                </p>
              </div>

              <div>
                <Label htmlFor="msp-cc" className="text-sm">
                  CC address <span className="text-white/40">(optional)</span>
                </Label>
                <Input
                  id="msp-cc"
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="alerts@yourtenant.com"
                  disabled={!enabled}
                  data-testid="msp-notifications-cc"
                  className="mt-1"
                />
                <p className="text-xs text-white/50 mt-1">
                  Optional CC recipient. Comma-separate multiple addresses if
                  needed.
                </p>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {savedAt && !error && (
              <p className="text-xs text-emerald-400">
                Saved {savedAt.toLocaleTimeString()}.
              </p>
            )}

            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving}
                data-testid="msp-notifications-save"
                className="flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                Save changes
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
